import { useCallback, useMemo, useState } from "react";
import "../../css/GlobalStyles.css";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../Containers/PopupModalContainer";
import {
  FormAnswersV2,
  FormInputType,
  FormResponseV2,
  NotificationType,
  SavedFormQuestion,
  SavedFormResponse,
} from "@markit/common.types";
import { createEmptyFormAnswersV2 } from "@markit/common.utils";
import RectangleButton from "../Buttons/RectangleButton";
import { Colors } from "../../utils/colors";
import { useDispatch, useSelector } from "react-redux";
import SingularQuestionInput from "../UserInfoContainers/SingularQuestionInput";
import { getAccountState } from "../../redux/slices/accountSlice";
import { API } from "../../API";
import isEqual from "lodash.isequal";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { getUserSavedFormResponsesRef } from "../../utils/FirebaseUtils";
import { onSnapshot, query, where } from "../../firebase";
import { FormResponsesProvider } from "../Form/FormResponsesContext";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import {
  finalizeFormFileUploadAnswers,
  getFileNameFromResponse,
} from "../../utils/eventUtils/formUtils";
import { CircularProgress } from "@mui/material";

interface SingleEditCustomDataProps {
  savedQuestion: SavedFormQuestion;
  savedFormResponse?: SavedFormResponse; // Either this state or eventFormResponse should be defined
  setSavedFormResponse?: (savedFormResponse: SavedFormResponse) => void;
  eventFormResponse?: FormResponseV2;
  setEventFormResponse?: (eventFormResponse: FormResponseV2) => void;
  eventId: string; // If defined, we update the event form response for the user, otherwise we update only the user saved form response
  userId: string;
  setModalVisible: (modalVisible: boolean) => void;
}

// Used to edit either top level savedFormResponse for a user, or an event specific form response
const SingleEditCustomData = (props: SingleEditCustomDataProps) => {
  const {
    savedQuestion,
    savedFormResponse,
    setSavedFormResponse,
    eventFormResponse,
    setEventFormResponse,
    eventId,
    userId,
    setModalVisible,
  } = props;
  const dispatch = useDispatch();
  const { savedQuestions, accountData } = useSelector(getAccountState).account;
  const [answers, setAnswers] = useState<FormAnswersV2>(
    eventFormResponse
      ? eventFormResponse.answers
      : createEmptyFormAnswersV2(
          [savedQuestion],
          savedFormResponse ? [savedFormResponse] : []
        )
  );
  const [formFileUploads, setFormFileUploads] = useState<
    Record<string, string>
  >({});
  const [loading, setLoading] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);

  useAsyncOnMount(async () => {
    if (savedQuestion.formInput.inputType === FormInputType.FILE_UPLOAD) {
      setLoadingFile(true);
      const fileName = await getFileNameFromResponse(
        answers[savedQuestion.id][0]
      );
      if (fileName) {
        setFormFileUploads((prev: Record<string, string>) => ({
          ...prev,
          [savedQuestion.id]: fileName,
        }));
      }
      setLoadingFile(false);
    }
  });

  const isEventFormResponse = useMemo(
    () => !!eventFormResponse,
    [eventFormResponse]
  );

  const originalAnswers = useMemo(
    () =>
      eventFormResponse
        ? eventFormResponse.answers
        : createEmptyFormAnswersV2(
            [savedQuestion],
            savedFormResponse ? [savedFormResponse] : []
          ),
    [eventFormResponse, savedQuestion, savedFormResponse]
  );

  // Checks if the answers array is empty or if all the answers are empty strings
  const noAnswersFound = useMemo(
    () =>
      answers[savedQuestion.id] &&
      (answers[savedQuestion.id].length === 0 ||
        !answers[savedQuestion.id].some((answer) => answer !== "")),
    [answers, savedQuestion.id]
  );

  const madeChanges = useMemo(() => {
    return !isEqual(answers, originalAnswers);
  }, [answers, originalAnswers]);

  const finalizeEdit = useCallback(() => {
    showNotificationBanner(
      dispatch,
      "Updated Answer",
      NotificationType.AFFIRMATIVE
    );
    setModalVisible(false);
    setLoading(false);
  }, [dispatch, setModalVisible]);

  const editOnPress = useCallback(async () => {
    setLoading(true);

    let currentAnswers = answers;
    try {
      currentAnswers = await finalizeFormFileUploadAnswers({
        creatorUid: accountData.uid,
        answers: currentAnswers,
        formQuestions: [savedQuestion],
        fileNamesByQuestionId: formFileUploads,
        setAnswers,
        eventId: eventId,
        followerUid: userId,
      });
    } catch (e: any) {
      setLoading(false);
      alert("Failed to upload a file. Please try again.");
      return;
    }

    const submittedAt = new Date().toISOString();
    await API.events.postFormResponse({
      eventId: isEventFormResponse ? eventId : "",
      uid: userId,
      creatorUid: accountData.uid,
      submittedAt,
      answers: currentAnswers,
    });
    // For event form response
    if (eventFormResponse && setEventFormResponse) {
      setEventFormResponse({
        ...eventFormResponse,
        answers: currentAnswers,
        submittedAt,
      });
      finalizeEdit();
    } else {
      // For user saved form response
      if (savedFormResponse) {
        setSavedFormResponse?.({
          ...savedFormResponse,
          lastResponded: submittedAt,
          responses: currentAnswers[savedQuestion.id],
        });
        finalizeEdit();
      } else {
        // Listen for new form response
        const savedFormResponsesRef = getUserSavedFormResponsesRef(
          accountData.uid,
          userId
        );
        const savedFormResponseQuery = query(
          savedFormResponsesRef,
          where("questionId", "==", savedQuestion.id)
        );
        const unsubscribe = onSnapshot(savedFormResponseQuery, (snapshot) => {
          if (!snapshot.empty) {
            const newFormResponse: SavedFormResponse = snapshot.docs[0].data();
            setSavedFormResponse?.(newFormResponse);
            finalizeEdit();
            // Unsubscribe after processing the data
            unsubscribe();
          }
        });
      }
    }
  }, [
    accountData.uid,
    answers,
    eventFormResponse,
    eventId,
    finalizeEdit,
    formFileUploads,
    isEventFormResponse,
    savedFormResponse,
    savedQuestion,
    setEventFormResponse,
    setSavedFormResponse,
    userId,
  ]);

  const editBackOnPress = useCallback(() => {
    setModalVisible(false);
    setAnswers(originalAnswers);
  }, [originalAnswers, setModalVisible]);

  return (
    <PopupModalContainer
      headerComp={<span className="sectionTitle">Edit Details</span>}
      noExit
      valueComp={
        loadingFile ? (
          <div className="Centering" style={{ paddingBlock: 30 }}>
            <CircularProgress style={{ color: Colors.GRAY1 }} size={20} />
          </div>
        ) : (
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <FormResponsesProvider
              formFileUploads={formFileUploads}
              setFormFileUploads={setFormFileUploads}
              formErrorIds={[]}
              setFormErrorIds={() => {}}
            >
              <SingularQuestionInput
                question={savedQuestion}
                formQuestions={savedQuestions}
                answers={answers}
                setAnswers={setAnswers}
                disabled={false}
              />
            </FormResponsesProvider>
          </div>
        )
      }
      fixedBottomContent={
        <div className="ColumnNormal">
          <div
            className="AlignedRow"
            style={{ gap: 10, paddingInline: 14, paddingTop: 14 }}
          >
            <RectangleButton
              buttonLabel={"Cancel"}
              onPress={editBackOnPress}
              altColor={Colors.WHITE1}
              altTextColor={Colors.BLACK}
              containerStyles={{
                paddingBlock: 14,
                paddingInline: 12,
                border: `0.5px solid ${Colors.GRAY2}`,
              }}
            />
            <RectangleButton
              buttonLabel={"Save"}
              onPress={editOnPress}
              altColor={Colors.BLACK}
              altTextColor={Colors.WHITE}
              containerStyles={{
                paddingBlock: 14,
                paddingInline: 12,
              }}
              disabled={!madeChanges || noAnswersFound}
              loading={loading}
            />
          </div>
        </div>
      }
      closeModal={editBackOnPress}
      modalContentWidth={PopupModalContainerWidth.SMALL}
    />
  );
};

export default SingleEditCustomData;
